<template>
  <Modal title="" :max-width="720" :header-border="false" body-overflow="auto" body-max-height="600px" :body-padding="false" @close="onClose">
    <template #header>
      <div class="cd__title-wrap">
        <span class="cd-source">
          <span class="cd-source__mark" style="background: #0d9dda; color: #fff;">
            <svg viewBox="0 0 16 16" fill="currentColor"><path d="M6.5 4.2a3 3 0 015.4 1 2.4 2.4 0 011.6 4.3 2.6 2.6 0 01-3 2 3.1 3.1 0 01-5.2-.4 2.3 2.3 0 01-2-3.9 2.7 2.7 0 013.2-3z"/></svg>
          </span>
          Salesforce
        </span>
        <div class="cd__name">{{ subjectDisplay }}</div>
        <div class="cd__sub">
          <template v-for="(seg, i) in subSegments" :key="i">
            <span v-if="i > 0" class="cd__dot" />
            <span>{{ seg }}</span>
          </template>
        </div>
      </div>
    </template>

    <div v-if="loading" class="cd-loading">Loading...</div>
    <div v-else-if="!eventData" class="cd-loading">Event not found.</div>
    <div v-else class="cd-cols">
      <!-- LEFT -->
      <div class="cd-cols__l">
        <!-- Time block -->
        <div class="cd-metrics">
          <div class="cd-metric">
            <span class="cd-metric__k">Start</span>
            <span class="cd-metric__v">{{ formatDateTime(eventData.start_datetime || eventData.activity_date) }}</span>
          </div>
          <div v-if="eventData.end_datetime" class="cd-vrule" />
          <div v-if="eventData.end_datetime" class="cd-metric">
            <span class="cd-metric__k">End</span>
            <span class="cd-metric__v">{{ formatDateTime(eventData.end_datetime) }}</span>
          </div>
        </div>

        <!-- Participants -->
        <div class="cd-notes">
          <div v-if="whoName" class="cd-note">
            <span class="cd-note__k">With</span>
            <span class="cd-note__v">{{ whoName }}</span>
          </div>

          <div v-if="ownerName" class="cd-note">
            <span class="cd-note__k">Owner</span>
            <span class="cd-note__v">{{ ownerName }}</span>
          </div>

          <div v-if="relatedName" class="cd-note">
            <span class="cd-note__k">Related to</span>
            <span class="cd-note__v">{{ relatedName }}</span>
          </div>

          <div v-if="locationDisplay" class="cd-note">
            <span class="cd-note__k">Location</span>
            <span class="cd-note__v">{{ locationDisplay }}</span>
          </div>
        </div>
      </div>

      <!-- RIGHT -->
      <div class="cd-cols__r">
        <div v-if="descriptionDisplay">
          <div class="cd-label">Description</div>
          <div class="cd-prose-inline">{{ descriptionDisplay }}</div>
        </div>
        <div v-else class="cd-empty">No description.</div>
      </div>
    </div>

    <template #footer>
      <button class="cd-btn cd-btn--ghost" @click="onClose">Close</button>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import Modal from '../../next/components/Modal.vue'
import { apiFetch } from '../../services/api'
import { useContextDrawer } from '../useContextDrawer'

const { contextGeneration } = useContextDrawer()

const props = defineProps<{
  accountId: string
  eventId: string
  initialEvent?: Record<string, any> | null
  onClose: () => void
}>()

const loading = ref(false)
const eventData = ref<Record<string, any> | null>(props.initialEvent || null)

const subjectDisplay = computed(() => {
  return String(eventData.value?.subject || 'Event').trim() || 'Event'
})

const subSegments = computed(() => {
  const parts: string[] = []
  parts.push('Event')
  const d = eventData.value
  if (d) {
    const raw = d.start_datetime || d.activity_date
    if (raw) {
      try {
        const dt = new Date(raw)
        parts.push(dt.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' }))
      } catch { /* skip */ }
    }
  }
  return parts
})

const whoName = computed(() => String(eventData.value?.who_name || eventData.value?.who_email || '').trim() || '')
const ownerName = computed(() => String(eventData.value?.owner_name || eventData.value?.owner_email || '').trim() || '')
const relatedName = computed(() => String(eventData.value?.what_name || eventData.value?.related_account_name || '').trim() || '')
const locationDisplay = computed(() => String(eventData.value?.location || '').trim() || '')
const descriptionDisplay = computed(() => String(eventData.value?.description || '').trim())

function formatDateTime(value: any): string {
  if (!value) return '—'
  try {
    const d = new Date(value)
    return d.toLocaleString([], { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })
  } catch {
    return String(value)
  }
}

async function loadEvent() {
  const accountId = String(props.accountId || '').trim()
  const eventId = String(props.eventId || '').trim()
  if (!accountId || !eventId) return
  if (props.initialEvent && String(props.initialEvent?.id || '').trim() === eventId) {
    eventData.value = props.initialEvent
    return
  }
  const gen = contextGeneration.value
  loading.value = true
  try {
    const resp = await apiFetch(`/accounts/${encodeURIComponent(accountId)}/salesforce_events/${encodeURIComponent(eventId)}`)
    if (gen !== contextGeneration.value) return
    eventData.value = resp?.event || null
  } catch {
    if (gen !== contextGeneration.value) return
    eventData.value = null
  } finally {
    if (gen === contextGeneration.value) {
      loading.value = false
    }
  }
}

watch(() => [props.accountId, props.eventId], () => {
  void loadEvent()
}, { immediate: true })
</script>

<style scoped>
.cd-loading {
  padding: var(--space-5);
  color: var(--color-text-tertiary);
  font-size: var(--fs-sm);
}

.cd-prose-inline {
  font-size: var(--fs-sm);
  line-height: var(--lh-loose);
  color: var(--color-text-primary);
  white-space: pre-wrap;
  word-break: break-word;
}

.cd-empty {
  font-size: var(--fs-sm);
  color: var(--color-text-tertiary);
  font-style: italic;
}
</style>
